import React, { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  Check,
  X,
  Loader2,
  Sliders,
  Users,
  Layers,
  Sparkles,
  FileSpreadsheet,
  Award,
} from 'lucide-react';
import { api } from '@/lib/api';

interface Plan {
  id: string;
  name: string;
  price: number;
  maxUsers: number;
  description: string | null;
  features: {
    bulkMatrix: boolean;
    gstComplianceExport: boolean;
    loyaltyEngine: boolean;
  };
  createdAt: string;
}

export function SubscriptionPlans() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form Fields
  const [name, setName] = useState('');
  const [price, setPrice] = useState<number>(1999);
  const [maxUsers, setMaxUsers] = useState<number>(5);
  const [description, setDescription] = useState('');
  const [bulkMatrix, setBulkMatrix] = useState(false);
  const [gstComplianceExport, setGstComplianceExport] = useState(false);
  const [loyaltyEngine, setLoyaltyEngine] = useState(false);

  // Modal / Toggle Panel
  const [createPanelOpen, setCreatePanelOpen] = useState(false);

  const fetchPlans = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/super/plans');
      if (res.data && res.data.success) {
        setPlans(res.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch subscription plans.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleCreatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Plan name is required.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        name: name.trim(),
        price: Number(price),
        maxUsers: Number(maxUsers),
        description: description.trim() || undefined,
        features: {
          bulkMatrix,
          gstComplianceExport,
          loyaltyEngine,
        },
      };

      const res = await api.post('/super/plans', payload);
      if (res.data && res.data.success) {
        setSuccess('Subscription plan created successfully!');
        setName('');
        setPrice(1999);
        setMaxUsers(5);
        setDescription('');
        setBulkMatrix(false);
        setGstComplianceExport(false);
        setLoyaltyEngine(false);
        setCreatePanelOpen(false);
        fetchPlans();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create subscription plan.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async (id: string, name: string) => {
    if (
      !window.confirm(
        `Are you absolutely sure you want to delete the plan "${name}"? Active tenants subscribed to this plan will revert to fallback STARTER bounds.`,
      )
    ) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await api.delete(`/super/plans/${id}`);
      setSuccess(`Plan "${name}" deleted successfully.`);
      fetchPlans();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete plan.');
    }
  };

  return (
    <div className="space-y-6 font-sans antialiased text-slate-800">
      {/* Dynamic Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-800 via-emerald-750 to-teal-900 p-6 sm:p-8 text-white shadow-lg border border-emerald-700/30">
        <div className="absolute right-0 top-0 -mr-16 -mt-16 w-64 h-64 bg-emerald-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/20 text-xs font-semibold text-emerald-250 mb-3">
              <Award size={14} className="animate-spin-slow" />
              <span>SaaS Revenue Control</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              SaaS Subscription Plans Manager
            </h2>
            <p className="mt-1 text-xs sm:text-sm text-emerald-100 font-medium">
              Create, customize, and configure subscription plans dynamically. Changes automatically
              propagate to platform metrics and licensing profiles.
            </p>
          </div>
          <button
            onClick={() => setCreatePanelOpen(!createPanelOpen)}
            className="self-start md:self-center px-4 py-2.5 bg-emerald-500 hover:bg-emerald-450 active:bg-emerald-600 text-white font-extrabold text-xs rounded-xl flex items-center gap-1.5 shadow-md shadow-emerald-950/20 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer border border-emerald-400/30"
          >
            <Plus size={16} />
            <span>Create Custom Plan</span>
          </button>
        </div>
      </div>

      {/* Messaging Feeds */}
      {error && (
        <div className="p-4 bg-rose-50 border border-rose-150 text-rose-800 rounded-xl text-xs font-bold shadow-sm flex items-center gap-2">
          <X className="w-4 h-4 shrink-0 text-rose-600" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-150 text-emerald-800 rounded-xl text-xs font-bold shadow-sm flex items-center gap-2">
          <Check className="w-4 h-4 shrink-0 text-emerald-600" />
          <span>{success}</span>
        </div>
      )}

      {/* Creation Drawer / Panel */}
      {createPanelOpen && (
        <form
          onSubmit={handleCreatePlan}
          className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm space-y-6 animate-fadeIn"
        >
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <div className="flex items-center gap-2">
              <Sliders size={20} className="text-emerald-600 animate-pulse" />
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                Configure New Dynamic Plan
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setCreatePanelOpen(false)}
              className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Core Fields */}
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Plan Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Diamond Infinite"
                  required
                  className="block w-full px-3 py-2 border border-slate-250 rounded-lg focus:outline-none focus:border-emerald-600 font-bold"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Monthly Price (INR)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-slate-400 font-bold">₹</span>
                  </div>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(Number(e.target.value))}
                    min="0"
                    required
                    className="block w-full pl-7 pr-3 py-2 border border-slate-250 rounded-lg focus:outline-none focus:border-emerald-600 font-bold font-mono"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Cashier Slots Bound
                </label>
                <input
                  type="number"
                  value={maxUsers}
                  onChange={(e) => setMaxUsers(Number(e.target.value))}
                  min="1"
                  required
                  className="block w-full px-3 py-2 border border-slate-250 rounded-lg focus:outline-none focus:border-emerald-600 font-bold font-mono"
                />
              </div>
            </div>

            {/* Description & Placement */}
            <div className="space-y-4">
              <div className="space-y-1 h-full flex flex-col">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Plan Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Briefly summarize plan highlights, features, and targeted shop scale..."
                  rows={4}
                  className="block w-full flex-1 px-3 py-2 border border-slate-250 rounded-lg focus:outline-none focus:border-emerald-600 text-xs font-semibold resize-none"
                />
              </div>
            </div>

            {/* Gated Features switches */}
            <div className="space-y-4">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block border-b border-slate-100 pb-1.5">
                Enable Gated Features
              </label>

              <div className="space-y-3">
                <label className="flex items-center justify-between p-2.5 bg-slate-55 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-2">
                    <Layers
                      size={16}
                      className={bulkMatrix ? 'text-emerald-600' : 'text-slate-400'}
                    />
                    <div>
                      <span className="text-xs font-bold text-slate-700 block">
                        Matrix Intake Grid
                      </span>
                      <span className="text-[9px] text-slate-400 font-semibold">
                        Enable color/size grid dimension builder.
                      </span>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={bulkMatrix}
                    onChange={(e) => setBulkMatrix(e.target.checked)}
                    className="w-4 h-4 text-emerald-650 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer"
                  />
                </label>

                <label className="flex items-center justify-between p-2.5 bg-slate-55 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet
                      size={16}
                      className={gstComplianceExport ? 'text-emerald-600' : 'text-slate-400'}
                    />
                    <div>
                      <span className="text-xs font-bold text-slate-700 block">
                        GST Governance Engine
                      </span>
                      <span className="text-[9px] text-slate-400 font-semibold">
                        Unlock GST billing, reports, and tax settings.
                      </span>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={gstComplianceExport}
                    onChange={(e) => setGstComplianceExport(e.target.checked)}
                    className="w-4 h-4 text-emerald-650 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer"
                  />
                </label>

                <label className="flex items-center justify-between p-2.5 bg-slate-55 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-2">
                    <Sparkles
                      size={16}
                      className={loyaltyEngine ? 'text-emerald-600' : 'text-slate-400'}
                    />
                    <div>
                      <span className="text-xs font-bold text-slate-700 block">Loyalty Engine</span>
                      <span className="text-[9px] text-slate-400 font-semibold">
                        Unlock user rewards and campaign logs.
                      </span>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={loyaltyEngine}
                    onChange={(e) => setLoyaltyEngine(e.target.checked)}
                    className="w-4 h-4 text-emerald-650 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer"
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setCreatePanelOpen(false)}
              className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-lg text-xs cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-emerald-650 hover:bg-emerald-700 text-white font-bold rounded-lg text-xs flex items-center gap-1.5 cursor-pointer shadow-md disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              <span>Create Plan</span>
            </button>
          </div>
        </form>
      )}

      {/* Grid of Dynamic Plans */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
          <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
            Resolving Active Plans...
          </span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((p) => {
            return (
              <div
                key={p.id}
                className="group relative bg-white border border-slate-200/85 rounded-2xl p-6 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all duration-200 flex flex-col justify-between"
              >
                <div className="space-y-4">
                  {/* Card Header */}
                  <div className="flex items-start justify-between">
                    <div className="space-y-0.5">
                      <h4 className="text-sm font-bold text-slate-800 tracking-tight group-hover:text-emerald-700 transition-colors">
                        {p.name}
                      </h4>
                      <p className="text-[10px] text-slate-400 font-medium">
                        Created {new Date(p.createdAt).toLocaleDateString()}
                      </p>
                    </div>

                    <button
                      onClick={() => handleDeletePlan(p.id, p.name)}
                      className="p-1.5 rounded-lg text-slate-350 hover:text-rose-500 hover:bg-rose-50 cursor-pointer transition-colors"
                      title="Delete Plan"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>

                  {/* Pricing Banner */}
                  <div className="py-3 px-4 bg-slate-50 border border-slate-150/60 rounded-xl flex items-baseline gap-1">
                    <span className="text-xl font-extrabold text-slate-800">
                      ₹{p.price.toLocaleString()}
                    </span>
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                      / Month
                    </span>
                  </div>

                  {/* Description */}
                  {p.description && (
                    <p className="text-xs text-slate-500 font-semibold leading-relaxed line-clamp-3">
                      {p.description}
                    </p>
                  )}

                  {/* Stats / Constraints */}
                  <div className="border-t border-slate-100 pt-3.5 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400 font-bold uppercase text-[9px] tracking-wider flex items-center gap-1.5">
                        <Users size={12} />
                        <span>Max Users Limit</span>
                      </span>
                      <span className="font-extrabold text-slate-700 font-mono bg-slate-50 px-2 py-0.5 rounded border border-slate-100">
                        {p.maxUsers}
                      </span>
                    </div>
                  </div>

                  {/* Feature Locks visualizer */}
                  <div className="border-t border-slate-100 pt-3.5 space-y-2">
                    <span className="text-slate-400 font-bold uppercase text-[9px] tracking-wider block">
                      Gated Modules Matrix
                    </span>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500 font-semibold">Bulk Matrix Grid</span>
                        {p.features.bulkMatrix ? (
                          <Check
                            size={14}
                            className="text-emerald-600 bg-emerald-50 rounded-full p-0.5"
                          />
                        ) : (
                          <X size={14} className="text-slate-300" />
                        )}
                      </div>

                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500 font-semibold">GST Governance Engine</span>
                        {p.features.gstComplianceExport ? (
                          <Check
                            size={14}
                            className="text-emerald-600 bg-emerald-50 rounded-full p-0.5"
                          />
                        ) : (
                          <X size={14} className="text-slate-300" />
                        )}
                      </div>

                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500 font-semibold">
                          Loyalty Campaign Engine
                        </span>
                        {p.features.loyaltyEngine ? (
                          <Check
                            size={14}
                            className="text-emerald-600 bg-emerald-50 rounded-full p-0.5"
                          />
                        ) : (
                          <X size={14} className="text-slate-300" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 border-t border-slate-100 pt-3.5 text-center">
                  <span className="text-[10px] text-emerald-650 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1 font-bold">
                    Active Subscription Plan
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
